import type { Theme } from "@emotion/react";
import type { FamilyGoalModelingPreview, ModelingTimelinePoint } from "Infrastructure/ApiZfp/Schema";

export type ChildrenChartData = {
  readonly totalMonthlyContribution: number;
  readonly totalLengthInMonths: number;
  readonly totalAmountInvested: number;
  readonly totalAmountRequired: number;
  readonly dataset: number[];
};

export const EARLY_AGE_CAP = 6;
export const PRIMARY_SCHOOL_AGE_CAP = 15;
export const HIGH_SCHOOL_AGE_CAP = 18;

const _timelineToDataset = (timeline: ModelingTimelinePoint[]): number[] => {
  const lengthInMonths = timeline.reduce((max, point) => Math.max(max, point.month), 0);
  const dataset = new Array(lengthInMonths + 1).fill(0);

  for (const point of timeline) {
    dataset[point.month] = point.value;
  }

  return dataset;
};

export const mapFamilyGoalModelingPreviewToChartData = (preview: FamilyGoalModelingPreview): ChildrenChartData => {
  const dataset = _timelineToDataset(preview.total.timeline);

  return {
    dataset,
    totalLengthInMonths: dataset.length,
    totalMonthlyContribution: preview.total.requiredMonthlyAmount,
    totalAmountInvested: preview.total.investedAmount,
    totalAmountRequired: preview.total.requiredAmount,
  };
};

export const getChildrenChartAnnotations = (
  theme: Theme,
  data: ChildrenChartData,
  earlyAgeWithdrawalIn?: number,
  primarySchoolWithdrawalIn?: number,
  highSchoolWithdrawalIn?: number,
  universityWithdrawalIn?: number,
  startToLifeWithdrawalIn?: number,
) => {
  const maxValue = Math.max(...data.dataset);

  const commonLineProps = {
    type: "line" as const,
    borderColor: theme.colors.border,
    borderWidth: 1,
  };

  // const commonLabelProps = {
  //   display: true,
  //   backgroundColor: "transparent",
  //   color: theme.colors.textSecondary,
  //   position: "start" as const,
  //   yAdjust: 0,
  //   font: {
  //     weight: 400,
  //     size: 10,
  //   },
  // };

  return {
    todayLine: {
      ...commonLineProps,
      xMin: 0,
      xMax: 0,
      yMin: 0,
      yMax: maxValue,
    },
    earlyAgeWithdrawalLine:
      (earlyAgeWithdrawalIn && {
        ...commonLineProps,
        xMin: earlyAgeWithdrawalIn * 12,
        xMax: earlyAgeWithdrawalIn * 12,
        yMin: 0,
        yMax: maxValue,
      }) ||
      undefined,
    primarySchoolWithdrawalLine:
      (primarySchoolWithdrawalIn && {
        ...commonLineProps,
        xMin: primarySchoolWithdrawalIn * 12,
        xMax: primarySchoolWithdrawalIn * 12,
        yMin: 0,
        yMax: maxValue,
      }) ||
      undefined,
    highSchoolWithdrawalLine:
      (highSchoolWithdrawalIn && {
        ...commonLineProps,
        xMin: highSchoolWithdrawalIn * 12,
        xMax: highSchoolWithdrawalIn * 12,
        yMin: 0,
        yMax: maxValue,
      }) ||
      undefined,
    universityWithdrawalLine:
      (universityWithdrawalIn && {
        ...commonLineProps,
        xMin: universityWithdrawalIn * 12,
        xMax: universityWithdrawalIn * 12,
        yMin: 0,
        yMax: maxValue,
      }) ||
      undefined,
    startToLifeWithdrawalLine:
      (startToLifeWithdrawalIn && {
        ...commonLineProps,
        xMin: startToLifeWithdrawalIn * 12,
        xMax: startToLifeWithdrawalIn * 12,
        yMin: 0,
        yMax: maxValue,
      }) ||
      undefined,
  };
};
